import { Link } from "react-router-dom";
import { PawPrint, Bone, Recycle, Leaf, Quote, Star, Facebook, Camera, MapPin, Info } from "lucide-react";
import { Icon } from "@/components/iconMap";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { EstimateButton, CallButton, TextButton } from "@/components/CTAButtons";
import { whyChoose, business, beforeAfter, serviceAreaCities, serviceAreaDisclaimer, assets } from "@/data/site";

export const SectionHeading = ({ eyebrow, title, sub, align = "left", light = false }) => (
  <div className={`${align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}`}>
    {eyebrow && (
      <span className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${light ? "text-gold" : "text-gold-dark"}`}>
        <PawPrint className="h-3.5 w-3.5" strokeWidth={2.4} /> {eyebrow}
      </span>
    )}
    <h2 className={`mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl ${light ? "text-cream" : "text-forest"}`}>{title}</h2>
    {sub && <p className={`mt-4 text-base leading-relaxed ${light ? "text-cream/75" : "text-charcoal/70"}`}>{sub}</p>}
  </div>
);

// Recurring conversion band.
export const EstimateCTASection = ({ compact = false }) => (
  <section className={`bg-forest ${compact ? "py-16" : "py-20"} relative overflow-hidden`}>
    <div className="grain pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true" />
    <div className="container-x relative">
      <Reveal className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold">
          <PawPrint className="h-3.5 w-3.5" strokeWidth={2.4} /> Need it gone?
        </span>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-cream sm:text-4xl">
          Need It Gone? Get a Free Estimate.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-cream/75">
          Tell us what you need removed and we'll help you determine the best option. For the fastest
          estimate, send us a few photos of the items.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <CallButton label="Call for a Free Estimate" size="lg" variant="primary" />
          <TextButton label="Text Photos for an Estimate" size="lg" variant="ghostLight" />
          <EstimateButton label="Request an Estimate Online" size="lg" variant="forest" className="border border-cream/25" to="/contact#estimate" />
        </div>
      </Reveal>
    </div>
  </section>
);

// Dog-treat promotion — tasteful, secondary to the junk-removal message.
export const DogTreatSection = () => (
  <section className="bg-cream py-20">
    <div className="container-x">
      <div className="grid items-center gap-10 rounded-lg border border-border bg-gradient-to-b from-white to-accent/40 p-8 sm:p-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">
            <Bone className="h-4 w-4" strokeWidth={2} /> A little bonus
          </span>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
            A Little Something for Your Best Friend
          </h2>
          <p className="mt-4 text-base leading-relaxed text-charcoal/75">
            Booking Benji's Hauling &amp; Junk Removal? If you have a dog at home, let us know! We'll bring
            along a complimentary treat bag for your furry friend as a small thank-you for choosing our team.
          </p>
          <p className="mt-4 rounded-md border border-border bg-cream px-4 py-3 text-xs leading-relaxed text-mutedgreen">
            Treat availability may vary. Please let us know about any known allergies or dietary restrictions.
          </p>
          <div className="mt-6">
            <EstimateButton label="Get Your Free Estimate" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="relative">
          <div className="group overflow-hidden rounded-lg border border-border">
            <img
              src={assets.benji}
              alt="Benji, the golden retriever, waiting for a treat"
              width="800" height="600" loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="animate-float absolute -bottom-4 -left-4 flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 shadow-sm">
            <Bone className="h-5 w-5 text-gold-dark" strokeWidth={2} />
            <span className="font-heading text-sm font-bold text-forest">Free treat with service</span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const WhyChooseSection = () => (
  <section className="bg-accent/40 py-20">
    <div className="container-x">
      <Reveal>
        <SectionHeading eyebrow="Why Benji's" title="Why Choose Benji's Hauling" align="center" sub="Friendly local service, done right, from the first call to the final sweep-up." />
      </Reveal>
      <StaggerGroup className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {whyChoose.map((w) => (
          <StaggerItem key={w.title} className="group rounded-lg border border-border bg-white p-6 transition-all hover:-translate-y-1 hover:border-gold/50 hover:shadow-md">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-forest/5 text-forest transition-colors group-hover:bg-gold/15 group-hover:text-gold-dark">
              <Icon name={w.icon} className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-forest">{w.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{w.body}</p>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

export const ResponsibleDisposalSection = () => (
  <section className="bg-cream py-20">
    <div className="container-x">
      <div className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <Reveal className="order-2 lg:order-1">
          <div className="overflow-hidden rounded-lg border border-border">
            <img
              src="https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?auto=format&fit=crop&w=800&q=70"
              alt="Sorted materials prepared for recycling and responsible disposal"
              width="800" height="600" loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-mutedgreen">
            <Recycle className="h-4 w-4" strokeWidth={2} /> Responsible disposal
          </span>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
            Hauling With a Little More Care
          </h2>
          <p className="mt-4 text-base leading-relaxed text-charcoal/75">
            When possible, we look for responsible ways to handle the items we collect rather than
            automatically sending everything to the landfill. Depending on the item and local options,
            this may include recycling, donation, or appropriate disposal.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {["Recycle when practical", "Donate usable items", "Dispose responsibly"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3.5 py-2 text-sm font-semibold text-forest">
                <Leaf className="h-4 w-4 text-mutedgreen" /> {t}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const BeforeAfterSection = () => (
  <section className="bg-accent/40 py-20">
    <div className="container-x">
      <Reveal>
        <SectionHeading eyebrow="Real results" title="Before & After" align="center" sub="Authentic project photos are on the way. These spots are reserved for Benji's real cleanouts." />
      </Reveal>
      <StaggerGroup className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2">
        {beforeAfter.map((b) => (
          <StaggerItem key={b.title} className="group overflow-hidden rounded-lg border border-border bg-white">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={b.img} alt={`${b.title} project example`} width="800" height="600" loading="lazy" className="h-full w-full object-cover grayscale-[15%] transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute left-3 top-3 rounded-md bg-forest/90 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-cream">Coming soon</span>
              <span className="absolute inset-0 flex items-center justify-center bg-charcoal/0 transition-colors group-hover:bg-charcoal/10">
                <Camera className="h-6 w-6 text-cream opacity-0 transition-opacity group-hover:opacity-90" />
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-heading text-base font-bold text-forest">{b.title}</h3>
              <p className="mt-1 text-xs text-mutedgreen">Photo placeholder</p>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>
      <Reveal className="mt-10 text-center">
        <EstimateButton label="Ready for Your Before & After? Get a Free Estimate" size="lg" />
      </Reveal>
    </div>
  </section>
);

export const ReviewsSection = () => (
  <section className="bg-cream py-20">
    <div className="container-x">
      <Reveal>
        <SectionHeading eyebrow="Testimonials" title="What Neighbors Are Saying" align="center" sub="We're just getting started. Real customer reviews will appear here as they come in, and we don't post anything we haven't earned." />
      </Reveal>
      <StaggerGroup className="mt-12 grid gap-5 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <StaggerItem key={i} className="rounded-lg border border-dashed border-border bg-white p-6">
            <Quote className="h-7 w-7 text-gold/50" />
            <div className="mt-3 flex gap-0.5 text-gold/40">
              {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}
            </div>
            <p className="mt-4 text-sm italic leading-relaxed text-mutedgreen">
              Customer Review Placeholder: your words could go here after we help clear your clutter.
            </p>
            <p className="mt-5 text-sm font-bold text-forest/70">Future Customer</p>
          </StaggerItem>
        ))}
      </StaggerGroup>
      <Reveal className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="reviews-facebook" className="inline-flex items-center gap-2 rounded-md border border-forest/25 px-6 py-3.5 text-sm font-semibold text-forest transition-all hover:-translate-y-0.5 hover:bg-forest hover:text-cream">
          <Facebook className="h-4 w-4" /> See More on Facebook
        </a>
      </Reveal>
    </div>
  </section>
);


const marqueeItems = ["Free Estimates", "Loyal Service", "Responsible Disposal", "Starting at $99", "Sweep-Up Included", "Benji Approved", "Dog-Friendly Bonus"];

export const MarqueeBand = () => (
  <div className="overflow-hidden border-y border-forest-light/40 bg-forest py-4" aria-hidden="true">
    <div className="flex w-max animate-marquee gap-8 hover:[animation-play-state:paused]">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex shrink-0 items-center gap-8">
          {marqueeItems.map((t) => (
            <span key={t} className="flex items-center gap-8">
              <span className="font-heading text-lg font-extrabold uppercase tracking-wide text-cream/90">{t}</span>
              <PawPrint className="h-5 w-5 shrink-0 text-gold" strokeWidth={2.2} />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);


export const ServiceAreaSection = ({ dark = false }) => (
  <section className={`${dark ? "bg-accent/40" : "bg-cream"} py-20`}>
    <div className="container-x">
      <Reveal>
        <SectionHeading
          eyebrow="Where we work"
          title="Serving Salem & Surrounding Areas"
          align="center"
          sub="Proudly hauling junk throughout the Mid-Willamette Valley. If you're nearby, we've got you covered."
        />
      </Reveal>
      <StaggerGroup className="mx-auto mt-10 flex max-w-3xl flex-wrap justify-center gap-2.5">
        {serviceAreaCities.map((c) => (
          <StaggerItem key={c}>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-forest">
              <MapPin className="h-3.5 w-3.5 text-gold-dark" /> {c}
            </span>
          </StaggerItem>
        ))}
        <StaggerItem>
          <span className="inline-flex items-center rounded-md border border-dashed border-gold/60 bg-gold/10 px-4 py-2 text-sm font-semibold text-gold-dark">
            + all areas in between
          </span>
        </StaggerItem>
      </StaggerGroup>
      <Reveal className="mx-auto mt-8 max-w-2xl rounded-lg border border-border bg-white p-6 text-center">
        <p className="flex items-start justify-center gap-2 text-sm leading-relaxed text-charcoal/75">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
          <span>{serviceAreaDisclaimer.intro}</span>
        </p>
        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-mutedgreen">
          Note: {serviceAreaDisclaimer.note}
        </p>
      </Reveal>
    </div>
  </section>
);
